/**
 * Object Type Store - Type CRUD actions
 */

import { generateId, getCurrentTimestamp, storage } from '../../lib/utils';
import { getStorageKey } from './persistence';
import type { ObjectType, ObjectTypeState } from './types';

type Get = () => ObjectTypeState;
type Set = (partial: Partial<ObjectTypeState>) => void;

export const createTypeActions = (set: Set, get: Get) => ({
  addType: (type: Omit<ObjectType, 'id' | 'created' | 'modified'> & { id?: string }) => {
    const state = get();

    // Validate name uniqueness
    const nameExists = state.types.some(
      (t) => t.name.toLowerCase() === type.name.toLowerCase()
    );

    if (nameExists) {
      throw new Error(`Object type with name "${type.name}" already exists`);
    }

    const now = getCurrentTimestamp();

    // Set default priority to 1 for "배경" type if not specified
    const isBackground = type.name === '배경' || type.name === 'background';

    const newType: ObjectType = {
      ...type,
      // API 생성 후 서버 발급 id 가 넘어오면 그대로 사용 (재진입 시 매칭 유지)
      id: type.id || generateId(),
      priority: isBackground && type.priority === undefined ? 1 : type.priority,
      created: now,
      modified: now,
    };

    const newTypes = [...state.types, newType];
    const newState = {
      types: newTypes,
      mappings: state.mappings,
    };

    // Persist to localStorage with current lot ID
    storage.set(getStorageKey(state.currentLotId), newState);

    set({ types: newTypes });
  },

  addTypes: (types: Omit<ObjectType, 'id' | 'created' | 'modified'>[]) => {
    const state = get();
    const now = getCurrentTimestamp();

    const newTypes = types.map((type) => {
      // Set default priority to 1 for "배경" type if not specified
      const isBackground = type.name === '배경' || type.name === 'background';

      return {
        id: generateId(),
        ...type,
        priority: isBackground && type.priority === undefined ? 1 : type.priority,
        created: now,
        modified: now,
      };
    });

    const allTypes = [...state.types, ...newTypes];
    const newState = {
      types: allTypes,
      mappings: state.mappings,
    };

    // Persist to localStorage with current lot ID
    storage.set(getStorageKey(state.currentLotId), newState);

    set({ types: allTypes });
  },

  updateType: (id: string, updates: Partial<ObjectType>) => {
    const state = get();
    const type = state.types.find((t) => t.id === id);

    if (!type) {
      throw new Error(`Object type with id "${id}" not found`);
    }

    // Validate name uniqueness if name is being updated
    if (updates.name) {
      const nameExists = state.types.some(
        (t) => t.id !== id && t.name.toLowerCase() === updates.name!.toLowerCase()
      );

      if (nameExists) {
        throw new Error(`Object type with name "${updates.name}" already exists`);
      }
    }

    const updatedType: ObjectType = {
      ...type,
      ...updates,
      modified: getCurrentTimestamp(),
    };

    const newTypes = state.types.map((t) => (t.id === id ? updatedType : t));
    const newState = {
      types: newTypes,
      mappings: state.mappings,
    };

    // Persist to localStorage with current lot ID
    storage.set(getStorageKey(state.currentLotId), newState);

    set({ types: newTypes });
  },

  clearTypes: () => {
    const state = get();
    const newState = {
      types: [],
      mappings: state.mappings,
    };

    // Persist to localStorage with current lot ID
    storage.set(getStorageKey(state.currentLotId), newState);

    set({ types: [] });
  },

  deleteType: (id: string) => {
    const state = get();

    // Check if type is in use (has mappings)
    const typeInUse = state.mappings.some((m) => m.typeId === id);

    if (typeInUse) {
      throw new Error('Cannot delete object type that is in use. Remove all mappings first.');
    }

    const newTypes = state.types.filter((t) => t.id !== id);
    const newState = {
      types: newTypes,
      mappings: state.mappings,
    };

    // Persist to localStorage with current lot ID
    storage.set(getStorageKey(state.currentLotId), newState);

    set({ types: newTypes });
  },

  getTypeById: (id: string) => {
    return get().types.find((t) => t.id === id);
  },
});
